import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, contentChild, input, output, TemplateRef } from '@angular/core'
import { Icon } from '../../icon'
import { IconButtonComponent } from '../../icon-button'
import { IconNewComponent } from '../../icon-new'
import { ToastType } from '../toast-type'

/**
 * A toast notification component that displays temporary messages to users. They are used to
 * notify users of actions they have taken or events that have occurred.
 *
 * @example
 * ```html
 * <ftr-toast-display
 *   [type]="ToastType.Success"
 *   title="Success!"
 *   message="Your changes have been saved."
 *   (closeToast)="onToastClose()"
 * />
 * ```
 */
@Component({
  selector: 'ftr-toast-display',
  templateUrl: './toast-display.component.html',
  styleUrl: './toast-display.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconNewComponent, IconButtonComponent, NgTemplateOutlet],
})
export class ToastDisplayComponent {
  readonly type = input<ToastType>(ToastType.Info)
  readonly title = input<string>('')
  readonly message = input<string>('')
  readonly hideClose = input<boolean>(false)

  readonly closeToast = output()

  readonly iconTemplate = contentChild<TemplateRef<any>>('iconTemplate')
  readonly messageTemplate = contentChild<TemplateRef<any>>('messageTemplate')

  protected readonly role = computed<string>(() => (this.type() === ToastType.Error ? 'alert' : 'status'))
  protected readonly ariaLive = computed<string>(() => (this.type() === ToastType.Error ? 'assertive' : 'polite'))
  protected readonly icon = computed<Icon>(() => iconMap[this.type()])
}

const iconMap: Record<ToastType, Icon> = {
  [ToastType.Info]: 'InfoCircleSolid',
  [ToastType.Success]: 'CheckmarkCircle',
  [ToastType.Warning]: 'Warning',
  [ToastType.Error]: 'ErrorCircleSolid',
} as const
